import { Dialog, DialogContent, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useToast } from "../../../components/ui/Toast";
import { useGetAssessmentQuery, useSetMuzakereDoneMutation } from "../../../api/personalApi";
import { SoftButton, SoftFooter, cls, useDialogPaperSx } from "../../hatim/soft/SoftKit";
import { memberRowState, muzakereDayState, todayString } from "../../../lib/muzakereWeek";
import { scoreText } from "../../../lib/assessment";
import { formatTurkishDate, parseLocalDate } from "../../../lib/dateUtils";
import { MuzakereIcon } from "../../../components/ui/mdiIcons";

function initials(p) {
  return `${(p.name || "").charAt(0)}${(p.surname || "").charAt(0)}`.toLocaleUpperCase("tr-TR");
}

function PersonList({ title, people, absent }) {
  return (
    <div className="pt-4">
      <div className={`px-5 pb-1.5 text-sm font-medium ${cls.muted}`}>{`${title} · ${people.length}`}</div>
      <ul>
        {people.map((p) => (
          <li key={p.id} className={`flex items-center gap-3 px-5 py-2 ${cls.row} ${absent ? "opacity-60" : ""}`}>
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${cls.avatar}`}>{initials(p)}</span>
            <span className={`text-base truncate ${cls.title}`}>{`${p.name || ""} ${p.surname || ""}`.trim()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InfoRow({ Icon, text }) {
  return (
    <SoftFooter className="items-center">
      <Icon fontSize="small" className={cls.muted} />
      <span className={`text-sm ${cls.muted}`}>{text}</span>
    </SoftFooter>
  );
}

/**
 * Müzakere ayrıntısı (mobil TerakkimCircles penceresi): başlık, grup ve
 * tarih, açıklama, katılanlar ve katılmayanlar. Sorusuz müzakerede yalnız
 * müzakerenin günü "Katıldım" işaretlenir ya da geri alınır; değerlendirmeli
 * müzakerede işaretleme yerine soruları cevaplama açılır.
 */
export default function MuzakereDialog({ item, onClose, onChanged }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const navigate = useNavigate();
  const paperSx = useDialogPaperSx();
  const [setDone, { isLoading: saving }] = useSetMuzakereDoneMutation();

  const row = item ? memberRowState(item.assessment) : "plain";
  const { data: assessResp } = useGetAssessmentQuery(
    { muzakereId: item?.id },
    { skip: !item || row !== "submitted" }
  );
  const myResponse = assessResp?.data?.myResponse;
  const score = myResponse ? scoreText(myResponse.score, t) : null;

  const today = todayString();
  const dayState = item ? muzakereDayState(item.date, today) : null;
  const dateObj = item ? parseLocalDate(item.date) : null;

  const mark = async (done) => {
    const result = await setDone({ muzakereId: item.id, done, today });
    if (result.error) {
      toast.error(result.error.message || t("muz_save_failed"));
      return;
    }
    toast.success(done ? t("muz_done_saved") : t("muz_done_undone"));
    if (onChanged) onChanged();
    onClose();
  };

  const openAssessment = () => {
    onClose();
    navigate(`/uygulama/muzakere/${item.id}/degerlendirme`);
  };

  let footer = null;
  if (item && row !== "plain") {
    if (row === "submitted") {
      footer = (
        <SoftFooter className="items-center">
          <CheckCircleIcon fontSize="small" className="text-[#2E9E6A] dark:text-[#7BE3A8]" />
          <span className={`flex-1 text-base ${cls.title}`}>{score ? `${t("muz_attended")} · ${score}` : t("muz_attended")}</span>
          <SoftButton tone="soft" onClick={openAssessment}>
            {t("muz_see_result")}
          </SoftButton>
        </SoftFooter>
      );
    } else if (row === "answer") {
      footer = (
        <SoftFooter>
          <SoftButton tone="success" fullWidth startIcon={<AssignmentOutlinedIcon />} onClick={openAssessment}>
            {t("muz_answer_questions")}
          </SoftButton>
        </SoftFooter>
      );
    } else {
      footer = (
        <InfoRow
          Icon={row === "notOpen" ? LockOutlinedIcon : ScheduleIcon}
          text={row === "notOpen" ? t("assess_not_open") : t("assess_closed")}
        />
      );
    }
  } else if (item) {
    if (item.done) {
      footer = (
        <SoftFooter className="items-center">
          <CheckCircleIcon fontSize="small" className="text-[#2E9E6A] dark:text-[#7BE3A8]" />
          <span className={`flex-1 text-base ${cls.title}`}>{t("muz_attended")}</span>
          {dayState === "open" ? (
            <SoftButton tone="cancel" onClick={() => mark(false)} disabled={saving}>
              {t("muz_undo")}
            </SoftButton>
          ) : null}
        </SoftFooter>
      );
    } else if (dayState === "open") {
      footer = (
        <SoftFooter>
          <SoftButton tone="success" fullWidth startIcon={<CheckIcon />} onClick={() => mark(true)} disabled={saving}>
            {t("muz_mark_done")}
          </SoftButton>
        </SoftFooter>
      );
    } else {
      footer = (
        <InfoRow
          Icon={dayState === "upcoming" ? LockOutlinedIcon : ScheduleIcon}
          text={dayState === "upcoming" ? t("muz_not_yet") : t("muz_expired")}
        />
      );
    }
  }

  return (
    <Dialog
      open={!!item}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { ...paperSx, maxHeight: "calc(100vh - 64px)" } }}
    >
      {item ? (
        <>
          <div className="flex items-center gap-3 px-5 pt-4 pb-3">
            <span className="w-10 h-10 rounded-full bg-terakkiPrimaryDark text-white flex items-center justify-center shrink-0">
              <MuzakereIcon sx={{ fontSize: 20 }} aria-hidden />
            </span>
            <div className="flex-1 min-w-0">
              <div className={`text-[15px] font-medium leading-snug line-clamp-2 ${cls.title}`}>{item.title}</div>
              <div className={`text-sm truncate ${cls.muted}`}>
                {[item.groupName, dateObj ? formatTurkishDate(dateObj) : item.date].filter(Boolean).join(" · ")}
              </div>
            </div>
            <IconButton size="small" onClick={onClose} disabled={saving} aria-label={t("close")}>
              <CloseIcon fontSize="small" className={cls.muted} />
            </IconButton>
          </div>
          <DialogContent sx={{ px: 0, pt: 0 }}>
            <p className={`px-5 text-base leading-5 whitespace-pre-line ${item.description ? cls.title : cls.muted}`}>
              {item.description || t("muz_no_description")}
            </p>
            {Array.isArray(item.participants) ? (
              item.participants.length === 0 ? (
                <div className="pt-4">
                  <div className={`px-5 pb-1.5 text-sm font-medium ${cls.muted}`}>{`${t("muz_participants")} · 0`}</div>
                  <p className={`px-5 text-base ${cls.muted}`}>{t("muz_no_participants")}</p>
                </div>
              ) : (
                <PersonList title={t("muz_participants")} people={item.participants} />
              )
            ) : null}
            {Array.isArray(item.absentees) && item.absentees.length > 0 ? (
              <PersonList title={t("muz_absentees")} people={item.absentees} absent />
            ) : null}
          </DialogContent>
          {footer}
        </>
      ) : null}
    </Dialog>
  );
}
